import { PACKAGE_STATUS } from '@/configs/constants'
import i18n from '@/i18n'
import moment from 'moment'

// workaround to use the mock API for local development (to decouple from communications module)
let PackagesApi
if (process.env.NODE_ENV === 'development') {
  PackagesApi = require('@/apis/mock/packages.js').default
} else {
  PackagesApi = require('@/apis/http/packages.js').default
}

const state = {
  packagePayload: null
}

const getters = {
  lastEvent: state => {
    if (!state.packagePayload) {
      return null
    }

    const lastStepIndex = state.packagePayload.steps.length - 1
    for (let i = lastStepIndex; i >= 0; i--) {
      var events = state.packagePayload.steps[i].events
      if (events !== null && events.length > 0) {
        return events[events.length - 1]
      }
    }
    return null
  },
  lastEventOfStepBySid: state => sid => {
    if (!state.packagePayload) {
      return null
    }

    const step = state.packagePayload.steps.find(step => step.info.sid === sid)
    if (step.events !== null && step.events.length > 0) {
      return step.events[step.events.length - 1]
    }
    return null
  },
  isRouteDrawable: state => state.packagePayload && state.packagePayload.isRouteDrawable,
  isDeliveryDateAvailable: (_state, getters) => getters.deliveryDateMessage !== null,
  deliveryDateMessage: state => {
    let currentDate = new Date()

    const minLeadDate = getMinLeadDate(state.packagePayload)
    const maxLeadDate = getMaxLeadDate(state.packagePayload)
    if (minLeadDate && maxLeadDate && maxLeadDate > currentDate && maxLeadDate > minLeadDate) {
      return deliveryMessageBetweenDates(minLeadDate, maxLeadDate)
    } else if (maxLeadDate && maxLeadDate > currentDate) {
      return deliveryMessageOnDate(maxLeadDate)
    } else {
      return null
    }
  }
}

const actions = {
  loadPackage ({ commit }, { country, trackingNumber }) {
    return PackagesApi.getPackage(country, trackingNumber).then((packagePayload) => {
      const formatedPackagePayload = formatPackagePayload(packagePayload.data)
      commit('setPackage', { packagePayload: formatedPackagePayload })
      return Promise.resolve(formatedPackagePayload)
    })
  }
}

const mutations = {
  setPackage (state, { packagePayload }) {
    state.packagePayload = packagePayload
  }
}

function formatPackagePayload (packagePayload) {
  if (!isPackagePayloadValid(packagePayload)) {
    return null
  }

  const packageStatus = packagePayload.statusLabel
  const hubSteps = packagePayload.steps.filter(step => step.info.type === 'HUB')
  const currentHub = packagePayload.currentHub

  let route = null
  let isRouteDrawable = isHubRouteDrawable(hubSteps)

  switch (packageStatus) {
    case PACKAGE_STATUS.AT_SOURCE:
      route = buildRouteOfPackageAtSource(hubSteps, packagePayload, isRouteDrawable)
      break
    case PACKAGE_STATUS.IN_TRANSIT:
      route = buildRouteOfPackageInTransit(hubSteps, currentHub, isRouteDrawable)
      break
    case PACKAGE_STATUS.AT_THE_HUB:
    case PACKAGE_STATUS.IN_EXCEPTION:
    case PACKAGE_STATUS.LOST:
    case PACKAGE_STATUS.CANCELLED:
    case PACKAGE_STATUS.REVERSED:
      route = buildRouteStandard(hubSteps, currentHub, isRouteDrawable)
      break
    case PACKAGE_STATUS.IN_PROGRESS:
    case PACKAGE_STATUS.IN_PROGRESS_SUCCESS:
    case PACKAGE_STATUS.IN_PROGRESS_FAILURE:
    case PACKAGE_STATUS.COMPLETED:
      route = buildRouteWithAllHubsAlreadyVisited(hubSteps, packagePayload, isRouteDrawable)
      break
  }

  injectPositionToRouteSteps(route)
  return Object.assign({}, packagePayload, route)
}

function isHubRouteDrawable (hubSteps) {
  return hubSteps.length > 0 &&
    hubSteps.every(hubStep =>
      hubStep.info &&
      hubStep.info.address &&
      hubStep.info.address.latitude &&
      hubStep.info.address.longitude)
}

function isPackagePayloadValid (packagePayload) {
  return (packagePayload && packagePayload.steps && packagePayload.currentHub && packagePayload.currentHub.sid)
}

function findStepIndexBySid (currentHubSid, steps) {
  return steps.map(step => step.info.sid).lastIndexOf(currentHubSid)
}

function buildRouteOfPackageAtSource (hubSteps, packagePayload, isRouteDrawable) {
  return {
    previousSteps: [],
    currentStep: packagePayload.steps.find(step => step.info.type === 'ORIGIN'),
    futureSteps: hubSteps,
    isRouteDrawable
  }
}

function buildRouteStandard (hubSteps, currentHub, isRouteDrawable) {
  let route = {
    previousSteps: [],
    futureSteps: [],
    currentStep: null,
    isRouteDrawable
  }
  const currentStepIndex = findStepIndexBySid(currentHub.sid, hubSteps)
  hubSteps.forEach((step, index) => {
    if (currentStepIndex === index) {
      route.currentStep = step
    } else if (!route.currentStep) {
      route.previousSteps.push(step)
    } else {
      route.futureSteps.push(step)
    }
  })
  return route
}

function buildRouteOfPackageInTransit (hubSteps, currentHub, isRouteDrawable) {
  let route = {
    previousSteps: [],
    futureSteps: [],
    currentStep: null,
    isRouteDrawable
  }
  const stepInTransitIndex = findStepIndexBySid(currentHub.sid, hubSteps)
  hubSteps.forEach((step, index) => {
    if (index < stepInTransitIndex) {
      route.previousSteps.push(step)
    } else {
      route.futureSteps.push(step)
    }
  })
  return route
}

function buildRouteWithAllHubsAlreadyVisited (hubSteps, packagePayload, isRouteDrawable) {
  return {
    previousSteps: hubSteps,
    currentStep: packagePayload.steps.find(step => step.info.type === 'TARGET'),
    futureSteps: [],
    isRouteDrawable
  }
}

function injectPositionToRouteSteps (route) {
  if (route.currentStep) {
    route.currentStep.position = 'CURRENT'
  }
  if (route.previousSteps) {
    route.previousSteps = route.previousSteps.map(step => Object.assign({}, step, { position: 'PREVIOUS' }))
  }
  if (route.futureSteps) {
    route.futureSteps = route.futureSteps.map(step => Object.assign({}, step, { position: 'FUTURE' }))
  }
}

function hasMinLeadDate (packagePayload) {
  return packagePayload &&
    packagePayload.order &&
    packagePayload.order.minLeadDate
}

function getMinLeadDate (packagePayload) {
  if (!hasMinLeadDate(packagePayload)) {
    return null
  }

  const minLeadDate = new Date(packagePayload.order.minLeadDate)
  return isNaN(minLeadDate) ? null : minLeadDate
}

function hasMaxLeadDate (packagePayload) {
  return packagePayload &&
    packagePayload.order &&
    packagePayload.order.maxLeadDate
}

function getMaxLeadDate (packagePayload) {
  if (!hasMaxLeadDate(packagePayload)) {
    return null
  }

  const maxLeadDate = new Date(packagePayload.order.maxLeadDate)
  return isNaN(maxLeadDate) ? null : maxLeadDate
}

function deliveryMessageBetweenDates (minLeadDate, maxLeadDate) {
  return i18n.t('PACKAGE.DELIVERY.BETWEEN_DATES', {
    earliestDeliveryDate: moment(minLeadDate).format('DD/MM/YYYY'),
    latestDeliveryDate: moment(maxLeadDate).format('DD/MM/YYYY')
  })
}

function deliveryMessageOnDate (maxLeadDate) {
  return i18n.t('PACKAGE.DELIVERY.ON_DATE', {
    latestDeliveryDate: moment(maxLeadDate).format('DD/MM/YYYY')
  })
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
